/**
 * People, as they appear in every picker: photo, name, and the email and role that tell
 * two people with the same name apart.
 */

import { useMemo, useState } from 'react';

import type { ComboOption } from '@/components/Combobox';
import { Avatar } from '@/components/ui';
import { useI18n } from '@/i18n';
import type { Job, Site, User } from '@/lib/api';
import { fullName } from '@/lib/format';
import { useLabel, type LabelFn } from '@/lib/useLabel';

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** A person as a picker option. `L` is the page's useLabel(), for the role's name. */
export function personOptions(users: User[], L: LabelFn, group?: string): ComboOption[] {
  return users.map((u) => ({
    value: u.id,
    label: fullName(u),
    sublabel: [u.email, L('role', u.role).label].filter(Boolean).join(' · '),
    leading: <Avatar name={fullName(u)} size={24} photoUrl={u.photoUrl} />,
    keywords: [u.jobTitle, u.badgeNumber].filter(Boolean).join(' '),
    ...(group ? { group } : {}),
  }));
}

/** A site as a picker option: its name, and where it is. */
export function siteOptions(sites: Site[], group?: string): ComboOption[] {
  return sites.map((s) => ({
    value: s.id,
    label: s.name,
    sublabel: [s.addressLine1, s.city].filter(Boolean).join(', ') || s.client?.name || undefined,
    keywords: s.client?.name ?? '',
    ...(group ? { group } : {}),
  }));
}

/** A job as a picker option: reference and title, then the site it is at. */
export function jobOptions(jobs: Job[]): ComboOption[] {
  return jobs.map((j) => ({
    value: j.id,
    label: `${j.reference} — ${j.title}`,
    sublabel: [j.site?.name, j.client?.name].filter(Boolean).join(' · ') || undefined,
    keywords: j.teamNames.join(' '),
  }));
}

/**
 * Choosing several people at once: a search box over a list of people, each with their
 * photo, email and role, ticked or not. The chosen ones stay at the top.
 */
export function PeopleMultiSelect({
  users,
  selected,
  onChange,
}: {
  users: User[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useI18n();
  const L = useLabel();
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const words = fold(query.trim()).split(/\s+/).filter(Boolean);
    const matches = users.filter((u) => {
      if (words.length === 0) return true;
      const hay = fold(`${fullName(u)} ${u.email ?? ''} ${L('role', u.role).label} ${u.jobTitle ?? ''}`);
      return words.every((w) => hay.includes(w));
    });
    return [...matches].sort(
      (a, b) => Number(selected.includes(b.id)) - Number(selected.includes(a.id)) || fullName(a).localeCompare(fullName(b), 'it'),
    );
  }, [users, query, selected, L]);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="p-2 border-b border-border bg-surface-alt">
        <input
          className="input h-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('common.searchPeople')}
        />
      </div>
      <ul className="max-h-60 overflow-y-auto divide-y divide-border">
        {visible.length === 0 ? (
          <li className="px-3 py-3 text-sm text-muted">{t('common.noMatches')}</li>
        ) : (
          visible.map((u) => {
            const on = selected.includes(u.id);
            return (
              <li key={u.id}>
                <label
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-surface-alt ${
                    on ? 'bg-[var(--color-primary-soft)]' : ''
                  }`}
                >
                  <input type="checkbox" checked={on} onChange={() => toggle(u.id)} />
                  <Avatar name={fullName(u)} size={28} photoUrl={u.photoUrl} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">{fullName(u)}</span>
                    <span className="block text-xs text-muted truncate">
                      {[u.email, L('role', u.role).label].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
